/**
 * Watchout_State and the display order (`complan-governance/requirements.md`
 * Requirement 7 criteria 2 and 9, Requirement 9 criterion 2).
 *
 * ## One predicate, and why the SQL asks it rather than restating it
 *
 * R7.2 derives the state from the resolution timestamp alone, and requires that
 * one derivation to serve the Detail_Panel, the Governance_Dashboard, the R6.11
 * filter and every export. {@link isWatchoutOpen} is it.
 *
 * `initiative-filter-query.ts` already established the technique for keeping a SQL
 * predicate honest to a domain one: rather than writing `resolved_at is not null`
 * independently, the query asks the domain predicate what a null resolution means
 * and picks its column predicate from the answer (that file does exactly this for
 * `isDependencyOpen`). So if "open" ever stops meaning "unresolved", the filter
 * changes with this function instead of silently disagreeing with it. There is no
 * `is_open` column and no `state` column in the schema for the same reason -
 * migration 26 stores only `resolved_at`.
 *
 * ## Severity ordering is the enum's, not a sort key
 *
 * `complan.watchout_severity` is declared `('low', 'medium', 'high')`, so
 * PostgreSQL's `order by severity desc` is already most-severe-first and needs no
 * sort-key column or CASE expression. {@link SEVERITY_RANK} is the JavaScript twin
 * of that declaration order, used by {@link compareWatchoutsForDisplay} so the
 * client and the database sort identically.
 */
/** The three severities of R7.1, in the declaration order of `complan.watchout_severity`. */
export const WATCHOUT_SEVERITIES = Object.freeze(['low', 'medium', 'high']);
/**
 * Ascending rank, matching the enum's declaration order exactly so that sorting
 * by descending rank in JavaScript and `order by severity desc` in PostgreSQL
 * produce the same sequence.
 */
export const SEVERITY_RANK = Object.freeze({
    low: 0,
    medium: 1,
    high: 2,
});
/**
 * Whether a Watchout is Open (R7.2).
 *
 * The single definition. An absent resolution timestamp is the Open state - the
 * same `NULL`-means-unset convention `initiative_dependency.completed_at` uses for
 * an open dependency.
 */
export function isWatchoutOpen(watchout) {
    return watchout.resolvedAt === null;
}
/** {@link isWatchoutOpen} as the two-valued state R7.2 names. */
export function watchoutState(watchout) {
    return isWatchoutOpen(watchout) ? 'open' : 'resolved';
}
/**
 * Whether a null `resolved_at` means Open.
 *
 * The indirection the SQL filter reads, so the column predicate is derived from
 * {@link isWatchoutOpen} rather than restated beside it. See this module's header
 * for why, and `initiative-filter-query.ts` for the precedent.
 */
export function nullResolutionMeansOpen() {
    return isWatchoutOpen({ resolvedAt: null });
}
/**
 * The Detail_Panel and roll-up order (R7.9, R9.2): Open before Resolved, then
 * descending severity, then ascending due date with absent due dates last.
 *
 * A null due date sorts last rather than first because a watchout with no due date
 * is the least time-pressured item in its severity band, and a reader scanning the
 * list is looking for what is imminent.
 */
export function compareWatchoutsForDisplay(a, b) {
    const openA = isWatchoutOpen(a);
    const openB = isWatchoutOpen(b);
    if (openA !== openB) {
        return openA ? -1 : 1;
    }
    const severity = SEVERITY_RANK[b.severity] - SEVERITY_RANK[a.severity];
    if (severity !== 0) {
        return severity;
    }
    if (a.dueDate === null && b.dueDate === null) {
        return 0;
    }
    if (a.dueDate === null) {
        return 1;
    }
    if (b.dueDate === null) {
        return -1;
    }
    // `YYYY-MM-DD` is fixed-width and zero-padded, so lexicographic order is
    // chronological order - the same property `PlainDate` comparisons rely on.
    return a.dueDate.localeCompare(b.dueDate);
}
/** Longest description R7.1 accepts, matching migration 26's `watchout_description_length`. */
export const MAX_WATCHOUT_DESCRIPTION_LENGTH = 1000;
/** Longest resolution note R7.1 accepts, matching migration 26's check. */
export const MAX_WATCHOUT_RESOLUTION_NOTE_LENGTH = 1000;
//# sourceMappingURL=watchout.js.map